import {
  MolsonBox,
  MolsonCustomTooltip,
  MolsonIcon,
  MolsonTypography
} from '@components/reusable'
import { styled, type Theme } from '@mui/material'
import { molsonUseTheme } from '@theme/customMuiExports'

type WizardStep = {
  name: string
  hasAlertIcon?: boolean
  hasWarningIcon?: boolean
  withTooltip?: {
    description: React.ReactNode
    title: string
  }
}

export type MolsonWizardStepsProps = {
  activeStep: number
  dataTestId?: string
  handleStepSwitch?: (step: number) => void
  stepsWithError?: number[]
  wizardSteps: WizardStep[]
  stepsDisabled?: number[]
}

type StepBorder = {
  index?: number
  isStepActive?: boolean
  isStepPassed?: boolean
  step?: number
  stepsWithError?: number[]
  theme?: Theme
}

const OuterContainer = styled(MolsonBox)({
  display: 'flex',
  height: 113,
  justifyContent: 'center',
  width: 'inherit'
})

const StepsContainer = styled(MolsonBox)({
  display: 'flex',
  gap: '16px',
  marginTop: '32px',
  width: '100%'
})

const stepContainerStyles = (disabled: boolean, clickable: boolean) => ({
  '&:hover': {
    cursor: (() => {
      if (disabled) {
        return 'not-allowed'
      }
      if (clickable) {
        return 'pointer'
      }
      return 'default'
    })()
  },
  display: 'flex',
  flex: 1,
  flexDirection: 'column'
})

const getStepBorderColor = ({
  isStepActive,
  isStepPassed,
  step,
  stepsWithError,
  theme
}: StepBorder) => {
  if (stepsWithError?.includes(Number(step))) {
    return isStepActive
      ? theme?.palette.primary.error
      : theme?.palette.primary.errorDisabled
  }
  if (isStepActive) {
    return theme?.palette.primary.light
  }
  if (isStepPassed) {
    return theme?.palette.primary.lightest
  }
  return theme?.palette.secondary[300]
}

const StepBorder = styled('div')<StepBorder>(
  ({ isStepActive, isStepPassed, step, stepsWithError, theme }) => ({
    '&': {
      borderTop: `4px solid ${getStepBorderColor({
        isStepActive,
        isStepPassed,
        step,
        stepsWithError,
        theme
      })}`,
      paddingBottom: 20,
      width: '100%'
    }
  })
)

export const MolsonWizardSteps = ({
  activeStep,
  dataTestId,
  handleStepSwitch,
  stepsWithError,
  wizardSteps,
  stepsDisabled
}: MolsonWizardStepsProps) => {
  const theme = molsonUseTheme()
  const isClickable = !!handleStepSwitch

  const getStepTextColor = (index: number, isStepDisabled: boolean) => {
    if (isStepDisabled) {
      return theme.palette.secondary[400]
    }
    if (stepsWithError?.includes(Number(index))) {
      return theme.palette.primary.error
    }
    return 'inherit'
  }

  return (
    <OuterContainer data-testid={dataTestId}>
      <StepsContainer>
        {wizardSteps?.map((step, index: number) => {
          const isStepDisabled = !!stepsDisabled?.includes(index)

          return (
            <MolsonBox
              key={`${step.name}-${index}`}
              onClick={
                isStepDisabled ? undefined : () => handleStepSwitch?.(index)
              }
              sx={stepContainerStyles(isStepDisabled, isClickable)}
            >
              <StepBorder
                isStepActive={activeStep === index}
                isStepPassed={activeStep > index - 1}
                step={index}
                stepsWithError={stepsWithError}
              />

              <MolsonBox display="flex" gap={0.5}>
                {step.hasAlertIcon && (
                  <MolsonIcon variant="ERROR_OUTLINE" iconColor="error" />
                )}
                {step.hasWarningIcon && (
                  <MolsonIcon variant="WARNING" iconColor="warning" />
                )}

                <MolsonTypography
                  color={getStepTextColor(index, isStepDisabled)}
                  fontSize="1.25rem"
                  fontWeight="bold"
                  variant="h4"
                >
                  {step.name}
                </MolsonTypography>
                {step.withTooltip && (
                  <MolsonCustomTooltip
                    config={{
                      description: step.withTooltip.description,
                      title: step.withTooltip.title
                    }}
                  />
                )}
              </MolsonBox>
            </MolsonBox>
          )
        })}
      </StepsContainer>
    </OuterContainer>
  )
}
